import React from "react";
import { useParams, Link } from "react-router-dom";
import { blogPosts } from "../data/blogPosts";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0A0D12] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-[#00D4AA] hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `https://osrey.com/blog/${post.slug}`;

  const handleShare = (platform: string) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    };
    window.open(urls[platform as keyof typeof urls], "_blank", "width=600,height=400");
  };

  return (
    <div className="min-h-screen bg-[#0A0D12] text-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-[#0A0D12] via-[#1C2A35] to-[#0A0D12]">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/blog"
            className="inline-flex items-center text-[#00D4AA] hover:underline mb-8"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Blog
          </Link>

          <div className="mb-6">
            <span className="px-4 py-2 bg-[#3A4E5F] rounded-full text-[#00D4AA] text-sm font-semibold">
              {post.category}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>

          <div className="flex items-center gap-6 text-gray-400 mb-8">
            <span>{post.date}</span>
            <span>•</span>
            <span>{post.readTime}</span>
            <span>•</span>
            <span>By {post.author}</span>
          </div>

          {/* Social Share Buttons */}
          <div className="flex items-center gap-4">
            <span className="text-gray-400">Share:</span>
            <button
              onClick={() => handleShare("twitter")}
              className="p-2 bg-[#1C2A35] hover:bg-[#3A4E5F] rounded-lg transition-colors"
              aria-label="Share on Twitter"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
              </svg>
            </button>
            <button
              onClick={() => handleShare("facebook")}
              className="p-2 bg-[#1C2A35] hover:bg-[#3A4E5F] rounded-lg transition-colors"
              aria-label="Share on Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
              </svg>
            </button>
            <button
              onClick={() => handleShare("linkedin")}
              className="p-2 bg-[#1C2A35] hover:bg-[#3A4E5F] rounded-lg transition-colors"
              aria-label="Share on LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-invert prose-lg max-w-none">
            {post.content.split("\n\n").map((paragraph, index) => {
              if (paragraph.startsWith("## ")) {
                return (
                  <h2
                    key={index}
                    className="text-3xl font-bold mt-12 mb-6 text-white"
                  >
                    {paragraph.replace("## ", "")}
                  </h2>
                );
              } else if (paragraph.startsWith("### ")) {
                return (
                  <h3
                    key={index}
                    className="text-2xl font-bold mt-8 mb-4 text-white"
                  >
                    {paragraph.replace("### ", "")}
                  </h3>
                );
              } else {
                return (
                  <p key={index} className="text-gray-300 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                );
              }
            })}
          </div>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-[#3A4E5F]">
            <div className="flex flex-wrap gap-3">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 bg-[#1C2A35] text-gray-300 rounded-full text-sm"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold mb-8">More Articles About Osrey</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {blogPosts
                .filter((p) => p.slug !== post.slug)
                .slice(0, 2)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-[#1C2A35] p-6 rounded-lg border border-[#3A4E5F] hover:border-[#00D4AA] transition-all duration-300"
                  >
                    <span className="text-sm text-[#00D4AA] mb-2 block">
                      {relatedPost.category}
                    </span>
                    <h4 className="text-xl font-bold mb-2 group-hover:text-[#00D4AA] transition-colors">
                      {relatedPost.title}
                    </h4>
                    <p className="text-gray-400 text-sm">{relatedPost.readTime}</p>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
